import { AsyncPipe, JsonPipe, NgIf } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import {
  ApplianceCategoryRadioListComponent,
  ApplianceSort,
  BrandCategoryApplianceRadioListComponent,
  BrandFilterListComponent,
  BrandSort,
  ModelNumberSelectTypeaheadComponent,
} from '@domgen/dgx-fe-catalogue';
import { ClientBrandCode } from '@domgen/dgx-fe-common';
import { CardComponent } from '@domgen/dgx-fe-components/card';
import { RichTextComponent } from '@domgen/dgx-fe-components/content';
import { TestHookDirective } from '@domgen/dgx-fe-components/general';
import { IconComponent } from '@domgen/dgx-fe-components/icon';
import { SectionLoadingDirective } from '@domgen/dgx-fe-components/loading';
import { PictureComponent } from '@domgen/dgx-fe-components/media';
import { PanelComponent } from '@domgen/dgx-fe-components/panel';
import {
  IpsAddressComponent,
  OverrideAddressComponent,
} from '@domgen/dgx-fe-forms/address';
import { CheckboxComponent } from '@domgen/dgx-fe-forms/checkbox';
import { CalendarInputComponent } from '@domgen/dgx-fe-forms/date';
import {
  CurrencyInputComponent,
  EmailInputComponent,
  TelephoneInputComponent,
  TextInputComponent,
} from '@domgen/dgx-fe-forms/input';
import {
  FormFieldComponent,
  FormFieldDirective,
  FormFieldsetComponent,
  FormLabelComponent,
  FormLegendComponent,
  FormRowComponent,
  FormSubmitButtonComponent,
} from '@domgen/dgx-fe-forms/layout';
import {
  DependantControlDirective,
  FormDisabledDirective,
  FormSubmitDirective,
} from '@domgen/dgx-fe-forms/shared';
import {
  SteppedFormComponent,
  SteppedFormFieldComponent,
} from '@domgen/dgx-fe-forms/stepped';
import {
  GAClickDirective,
  GAContainerDirective,
  GAEvent,
  GAFormFieldDirective,
  GAService,
} from '@domgen/dgx-fe-ga';
import {
  provideTranslationDictionaryMapping,
  TranslatePipe,
} from '@domgen/dgx-fe-i18n';
import { QMSanitiseDirective } from '@domgen/dgx-fe-qm';
import {
  ApplianceDetailsSteppedIpsFormValue,
  RegistrationCodeFormValue,
  RegistrationRequest,
} from '@us-registration/util-models';
import { translation } from '../../i18n/translation.constant';
import { ApplianceDetailsSteppedTestHooks } from '../../models/test/appliance-details-stepped-test-hooks.enum';
import { ApplianceDetailsSteppedWibFormBuilderService } from '../../services/appliance-details-stepped-wib-form-builder/appliance-details-stepped-wib-form-builder.service';
import { ApplianceDetailsSteppedWibFormComponentService } from '../../services/appliance-details-stepped-wib-form-component/appliance-details-stepped-wib-form-component.service';
import { SerializationService } from '../../services/serialization/serialization.service';
import { ApplianceDetailsSteppedWibContentToggleComponent } from '../appliance-details-stepped-wib-content-toggle/appliance-details-stepped-wib-content-toggle.component';
import { ApplianceDetailsSteppedWibFormFindModelSerialPanelComponent } from '../appliance-details-stepped-wib-form-find-model-serial-panel/appliance-details-stepped-wib-form-find-model-serial-panel.component';
import { ApplianceDetailsSteppedWibFormRegistrationCodeConfirmationPanelWibComponent } from '../appliance-details-stepped-wib-form-registration-code-confirmation-panel-wib/appliance-details-stepped-wib-form-registration-code-confirmation-panel-wib.component';

@Component({
  selector: 'us-registration-appliance-details-stepped-wib-form',
  templateUrl: './appliance-details-stepped-wib-form.component.html',
  styleUrls: ['./appliance-details-stepped-wib-form.component.scss'],
  providers: [
    ApplianceDetailsSteppedWibFormBuilderService,
    provideTranslationDictionaryMapping(translation),
    SerializationService,
    ApplianceDetailsSteppedWibFormComponentService,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ApplianceCategoryRadioListComponent,
    ApplianceDetailsSteppedWibContentToggleComponent,
    ApplianceDetailsSteppedWibContentToggleComponent,
    ApplianceDetailsSteppedWibFormFindModelSerialPanelComponent,
    ApplianceDetailsSteppedWibFormFindModelSerialPanelComponent,
    ApplianceDetailsSteppedWibFormRegistrationCodeConfirmationPanelWibComponent,
    AsyncPipe,
    BrandCategoryApplianceRadioListComponent,
    BrandFilterListComponent,
    CalendarInputComponent,
    CardComponent,
    CheckboxComponent,
    CurrencyInputComponent,
    DependantControlDirective,
    EmailInputComponent,
    FormDisabledDirective,
    FormFieldComponent,
    FormFieldDirective,
    FormFieldsetComponent,
    IpsAddressComponent,
    FormLabelComponent,
    FormLegendComponent,
    FormRowComponent,
    FormSubmitButtonComponent,
    FormSubmitDirective,
    GAClickDirective,
    GAContainerDirective,
    GAFormFieldDirective,
    IconComponent,
    ModelNumberSelectTypeaheadComponent,
    NgIf,
    JsonPipe,
    OverrideAddressComponent,
    PanelComponent,
    PictureComponent,
    QMSanitiseDirective,
    ReactiveFormsModule,
    RichTextComponent,
    SectionLoadingDirective,
    SteppedFormComponent,
    SteppedFormFieldComponent,
    TelephoneInputComponent,
    TestHookDirective,
    TextInputComponent,
    TranslatePipe,
  ],
})
export class ApplianceDetailsSteppedWibFormComponent
  implements OnInit, AfterViewInit
{
  @Input() set clientBrandCode(clientBrandCode: ClientBrandCode | undefined) {
    this.cs.updateClientBrandCode(clientBrandCode);
  }

  @Output() activeStep = new EventEmitter<number>();
  @Output() submitted = new EventEmitter<RegistrationRequest>();
  @ViewChild(SteppedFormComponent) steppedFormComponent?: SteppedFormComponent;

  applianceSort = ApplianceSort;
  brandSort = BrandSort;
  hasModelSerialNumber = true;
  registrationHasModelAndSerialNumber = true;
  testHooks = ApplianceDetailsSteppedTestHooks;

  constructor(
    readonly cs: ApplianceDetailsSteppedWibFormComponentService,
    private readonly destroyRef: DestroyRef,
    private readonly gaService: GAService
  ) {}

  ngOnInit(): void {
    this.cs.buildForm();
    this.cs.buildRegistrationCodeForm();
    this.cs.listenToRouteFragmentChanges();
  }

  ngAfterViewInit(): void {
    this.cs.updateSteppedFormComponent(this.steppedFormComponent);
  }

  toggleHasRegistrationCode(hasRegistrationCode: boolean): void {
    this.cs.updateHasRegistrationCode(hasRegistrationCode);
  }

  toggleHasModelSerialNumber(): void {
    this.hasModelSerialNumber = !this.hasModelSerialNumber;
  }

  onFormSubmit(formValue: ApplianceDetailsSteppedIpsFormValue): void {
    this.cs
      .onFormSubmit(formValue)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((registrationRequest: RegistrationRequest) => {
        this.submitted.emit(registrationRequest);
        this.gaService.tag({
          pagename: 'appliance-enter-personal-details',
          eventLabel: 'Enter your details',
          eventAction: 'Continue',
          eventCategory: 'Continue button',
          event: GAEvent.GenericEvent,
        });
      });
  }

  onRegistrationCodeFormSubmit(formValue: RegistrationCodeFormValue): void {
    this.cs
      .onRegistrationCodeSubmit(formValue)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.cs.updateHasRegistrationCode(false);
        this.cs.updateNavigatedToPriceStep(false);
      });
  }
}
